import { z } from "zod";
import { SchedulingMessageAttentionSchema } from "./patient-messaging.js";
import { PatientAdministrativeSexSchema } from "./patient-profile.js";
import { PatientQuestionnaireDraftSchema, PatientQuestionnaireSchema, } from "./patient-questionnaire.js";
import { IanaTimeZoneSchema, IdempotencyKeySchema, IsoDateTimeSchema } from "./primitives.js";
const DemoSchedulingIdSchema = z.string().trim().min(1).max(100);
const LocalTimeSchema = z
    .string()
    .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "Time must use 24-hour HH:mm format");
const LocalDateSchema = z.iso.date();
const ShortTextSchema = z.string().trim().min(1).max(240);
const OptionalTextSchema = z.string().trim().max(1_000);
/**
 * Totals the durable schedule never exceeds: the SQL saves enforce the same numbers, so the
 * strict read of a clinic's schedule cannot fail on size. Ids the server generates stay within
 * `idLength` too.
 */
export const SCHEDULING_LIMITS = {
    idLength: 100,
    providers: 100,
    appointmentTypes: 100,
    /** Visit types (and duration overrides) one availability window lists. */
    appointmentTypesPerWindow: 30,
    /** Weekly availability rules across the clinic. */
    availabilityRules: 500,
    availabilityRulesPerProvider: 70,
    /** Date overrides from the clinic's today onward, across the clinic. */
    availabilityOverrides: 1_000,
    availabilityOverridesPerProvider: 366,
    /** Date overrides fall from the clinic's today to this many days ahead. */
    availabilityOverrideHorizonDays: 366,
    /** Blocks one schedule read returns: a bounded window, never every block ever made. */
    stateBlocks: 2_000,
    /** Affected appointments one acknowledgement lists by id; the count covers the rest. */
    acknowledgementAppointmentIds: 500,
    /** Websites one clinic lists as allowed to show its booking widget (PA-12). */
    websiteOrigins: 5,
    /** Characters in one listed website (WebsiteOriginSchema). */
    websiteOriginLength: 200,
};
/**
 * One website allowed to show a clinic's booking widget (PA-12): an exact https origin in the
 * form a browser reports it (location.ancestorOrigins, new URL(...).origin), so a listed site
 * matches by plain string comparison. Lowercase, a dotted host name of letters, digits and inner
 * hyphens (an international name in its xn-- form), and a port only when it is not the default:
 * 1-65535 without leading zeros, never an explicit :443, which a browser never reports. No path,
 * trailing slash, query, credentials or wildcard. A string, so the SQL settings save can copy it
 * verbatim (scripts/supabase parity test); it compiles the same in PostgreSQL and in JavaScript,
 * also with the v flag of an HTML pattern attribute (no hyphen inside a character class).
 */
export const WEBSITE_ORIGIN_PATTERN = "^https://[a-z0-9]+(?:-+[a-z0-9]+)*(?:\\.[a-z0-9]+(?:-+[a-z0-9]+)*)+" +
    "(?::(?:[1-9][0-9]?|[1-35-9][0-9]{2}|4[0-35-9][0-9]|44[0-24-9]|[1-9][0-9]{3}|[1-5][0-9]{4}" +
    "|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5]))?$";
export const WebsiteOriginSchema = z
    .string()
    .max(SCHEDULING_LIMITS.websiteOriginLength)
    .regex(new RegExp(WEBSITE_ORIGIN_PATTERN), "Website must be an https address with no path, such as https://www.example.ca");
/** A clinic's widget websites: at most SCHEDULING_LIMITS.websiteOrigins, each listed once. */
export const WebsiteOriginsSchema = z
    .array(WebsiteOriginSchema)
    .max(SCHEDULING_LIMITS.websiteOrigins)
    .refine((origins) => new Set(origins).size === origins.length, "Each website can be listed only once");
export const DemoPublicBookingPathSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^\/[A-Za-z0-9][A-Za-z0-9._~-]*(?:\/[A-Za-z0-9][A-Za-z0-9._~-]*)*\/?$/, "Public booking path must be a non-root relative path without a query or fragment")
    .refine((value) => !/^\/intake(?:\/|$)/i.test(value), "Public booking path cannot use the reserved scheduled-intake route");
export const DemoSchedulingModeSchema = z.enum(["walk_in_only", "hybrid", "scheduled_only"]);
/**
 * Who may book online while online booking is on. "Nobody" is onlineBookingEnabled = false.
 * Existing patients prove it with their health card number and date of birth.
 */
export const DemoOnlineBookingAudienceSchema = z.enum(["anyone", "existing_patients"]);
/**
 * Who picks the physician for an online booking. With clinic_assigns the clinic books the
 * least-loaded eligible physician and the patient sees no physician; per_appointment_type
 * defers to each visit type's own physicianChoice.
 */
export const DemoPhysicianChoiceSchema = z.enum([
    "patient_chooses",
    "clinic_assigns",
    "per_appointment_type",
]);
/** A visit type's own choice; it applies only while the clinic sets per_appointment_type. */
export const DemoAppointmentTypePhysicianChoiceSchema = z.enum([
    "patient_chooses",
    "clinic_assigns",
]);
/**
 * How close to the visit patients may still cancel or change the time online (PA-9, lead
 * decision L4): while now is before the start minus this many minutes; 0 means until the visit
 * starts. A new time must also start at least this far ahead. Staff are never limited, and the
 * public booking configuration never carries it (a manage link's session says what the patient
 * may do: PublicAppointmentSelfServiceSchema).
 */
export const DemoCancellationCutoffMinutesSchema = z.number().int().min(0).max(43_200);
/**
 * How patients change the time of a booking (PA-9, lead decision L3). online: from their manage
 * link they pick a new time, by the same rules as booking (the cancellation cutoff, minimum lead,
 * online booking on, and the visit type and patient still bookable online). contact_clinic: the
 * manage page asks them to call the clinic; they can still confirm and cancel online.
 */
export const PatientReschedulingSchema = z.enum(["online", "contact_clinic"]);
/**
 * Why a patient cancelled online (PA-9): one of five fixed choices, never free text. Stored on
 * the booking and shown to staff (PATIENT_CANCELLATION_REASON_STAFF_LABELS); never in audit
 * metadata, logs or messages.
 */
export const PatientCancellationReasonSchema = z.enum([
    /** The time no longer works. */
    "schedule_conflict",
    /** The patient feels better. */
    "feeling_better",
    /** The patient booked care elsewhere. */
    "booked_elsewhere",
    /** The patient cannot get there. */
    "transportation",
    "other",
]);
/** How staff read a patient's cancellation reason (lead decision L8). */
export const PATIENT_CANCELLATION_REASON_STAFF_LABELS = {
    schedule_conflict: "Can't make the time",
    feeling_better: "Feeling better",
    booked_elsewhere: "Booked care elsewhere",
    transportation: "Transportation",
    other: "Other",
};
export const DemoSchedulingWeekdaySchema = z.enum([
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
]);
export const DemoSchedulingVisitModeSchema = z.enum(["in_person", "phone", "video"]);
export const DemoAppointmentStatusSchema = z.enum([
    "booked",
    "confirmed",
    "checked_in",
    "in_progress",
    "completed",
    "cancelled",
    "no_show",
]);
export const DemoAppointmentSourceSchema = z.enum(["front_desk", "phone", "follow_up", "online"]);
/**
 * Where a durable scheduling row came from: a booking's source, or `walk_in`, the row a walk-in
 * visit gets when the desk first sends it a questionnaire link (PA-5). Walk-in rows have no
 * physician or visit type, so they are never on Schedule's appointments list, in the demo
 * scheduler, or among booking sources staff choose; reads that list any durable row (the
 * questionnaire review list, a chart's consent history) use this schema.
 */
export const SchedulingAppointmentSourceSchema = z.enum([
    ...DemoAppointmentSourceSchema.options,
    "walk_in",
]);
export const DemoReminderChannelSchema = z.enum(["sms", "email"]);
export const DemoReminderKindSchema = z.enum(["confirmation", "reminder"]);
export const DemoScheduledIntakeStatusSchema = z.enum([
    "not_required",
    "required",
    "sent",
    "completed",
]);
export const DemoScheduledIntakeDeliverySchema = z.enum(["staff_link", "email"]);
export const DemoScheduledIntakeTokenSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{24,128}$/, "Scheduled intake token is invalid");
export const DemoScheduledIntakePathSchema = z
    .string()
    .trim()
    .regex(/^\/intake\/[A-Za-z0-9_-]{24,128}$/, "Scheduled intake path must contain one opaque token");
export const DemoSchedulingHoursSchema = z
    .object({
    weekday: DemoSchedulingWeekdaySchema,
    enabled: z.boolean(),
    startTime: LocalTimeSchema,
    endTime: LocalTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.enabled && value.startTime >= value.endTime) {
        context.addIssue({
            code: "custom",
            message: "Open hours must end after they start",
            path: ["endTime"],
        });
    }
});
export const DemoScheduleProviderSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    displayName: ShortTextSchema,
    credentials: z.string().trim().min(1).max(80),
    color: z.string().regex(/^#[a-fA-F0-9]{6}$/),
    active: z.boolean(),
    acceptingOnlineBookings: z.boolean(),
    /**
     * The version of this physician's calendar (weekly rules and date overrides). The durable
     * store owns it and each calendar save bumps it; a settings save ignores it.
     */
    availabilityVersion: z.number().int().positive().optional(),
})
    .strict();
export const DemoAppointmentTypeSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    name: ShortTextSchema,
    description: z.string().trim().min(1).max(500),
    durationMinutes: z.number().int().min(5).max(240),
    bufferBeforeMinutes: z.number().int().min(0).max(120),
    bufferAfterMinutes: z.number().int().min(0).max(120),
    color: z.string().regex(/^#[a-fA-F0-9]{6}$/),
    visitMode: DemoSchedulingVisitModeSchema,
    active: z.boolean(),
    patientBookable: z.boolean(),
    newPatientsAllowed: z.boolean(),
    /** The default reads types stored before the setting existed; settings saves send it. */
    physicianChoice: DemoAppointmentTypePhysicianChoiceSchema.default("patient_chooses"),
})
    .strict();
const availabilityWindowShape = {
    startTime: LocalTimeSchema,
    endTime: LocalTimeSchema,
    /** Empty means every visit type. */
    appointmentTypeIds: z
        .array(DemoSchedulingIdSchema)
        .max(SCHEDULING_LIMITS.appointmentTypesPerWindow),
    durationOverrides: z
        .array(z
        .object({
        appointmentTypeId: DemoSchedulingIdSchema,
        durationMinutes: z.number().int().min(5).max(240),
    })
        .strict())
        .max(SCHEDULING_LIMITS.appointmentTypesPerWindow)
        .default([]),
};
function refineAvailabilityWindow(value, context) {
    if (value.startTime >= value.endTime) {
        context.addIssue({
            code: "custom",
            message: "Provider availability must end after it starts",
            path: ["endTime"],
        });
    }
    if (new Set(value.appointmentTypeIds).size !== value.appointmentTypeIds.length) {
        context.addIssue({
            code: "custom",
            message: "Appointment types must be unique within an availability window",
            path: ["appointmentTypeIds"],
        });
    }
    const overrideIds = value.durationOverrides.map((override) => override.appointmentTypeId);
    if (new Set(overrideIds).size !== overrideIds.length) {
        context.addIssue({
            code: "custom",
            message: "Duration overrides must reference each appointment type at most once",
            path: ["durationOverrides"],
        });
    }
}
/** Overlapping windows on one day may not both offer a visit type (empty offers every type). */
function windowsOverlap(left, right) {
    if (left.startTime >= right.endTime || right.startTime >= left.endTime)
        return false;
    return (left.appointmentTypeIds.length === 0 ||
        right.appointmentTypeIds.length === 0 ||
        left.appointmentTypeIds.some((id) => right.appointmentTypeIds.includes(id)));
}
function refineNoOverlaps(windows, sameDay, path, message, context) {
    windows.forEach((left, leftIndex) => {
        windows.slice(leftIndex + 1).forEach((right, offset) => {
            if (sameDay(left, right) && windowsOverlap(left, right)) {
                context.addIssue({ code: "custom", message, path: [path, leftIndex + offset + 1] });
            }
        });
    });
}
export const DemoProviderAvailabilityRuleSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    providerId: DemoSchedulingIdSchema,
    weekday: DemoSchedulingWeekdaySchema,
    ...availabilityWindowShape,
})
    .strict()
    .superRefine(refineAvailabilityWindow);
/**
 * One physician's hours on one clinic-local date. A date's overrides replace that date's weekly
 * rules for that physician and stay inside clinic hours, closures and leave.
 */
export const DemoAvailabilityOverrideSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    providerId: DemoSchedulingIdSchema,
    date: LocalDateSchema,
    ...availabilityWindowShape,
})
    .strict()
    .superRefine(refineAvailabilityWindow);
export const DemoReminderPolicySchema = z
    .object({
    confirmationEnabled: z.boolean(),
    smsEnabled: z.boolean(),
    emailEnabled: z.boolean(),
    reminderOffsetsMinutes: z.array(z.number().int().min(15).max(43_200)).max(6),
    provider: z.literal("synthetic_placeholder"),
})
    .strict();
/**
 * Clinic-wide settings. Rows stored before PA-4 lack the newer keys, so reads default them;
 * SchedulingSettingsSchema (the write) requires every one.
 */
const schedulingSettingsShape = {
    version: z.number().int().positive(),
    tenantId: DemoSchedulingIdSchema,
    siteId: DemoSchedulingIdSchema,
    clinicName: ShortTextSchema,
    clinicAddress: ShortTextSchema,
    timeZone: IanaTimeZoneSchema,
    mode: DemoSchedulingModeSchema,
    onlineBookingEnabled: z.boolean(),
    onlineBookingAudience: DemoOnlineBookingAudienceSchema.default("anyone"),
    /** Applies only while onlineBookingAudience is anyone. */
    allowNewPatientsOnline: z.boolean(),
    /** Snapshotted onto each booking; changing it never rewrites booked appointments. */
    newPatientQuestionnaireRequired: z.boolean().default(true),
    physicianChoice: DemoPhysicianChoiceSchema.default("patient_chooses"),
    cancellationCutoffMinutes: DemoCancellationCutoffMinutesSchema.default(0),
    /**
     * PA-9. Read as online when absent: the store does not backfill it (lead decision L3), so a
     * clinic saved before PA-9 reads the same in every release.
     */
    patientRescheduling: PatientReschedulingSchema.default("online"),
    bookingWindowDays: z.number().int().min(1).max(365),
    minimumLeadMinutes: z.number().int().min(0).max(43_200),
    slotIntervalMinutes: z.number().int().min(5).max(60),
    clinicHours: z.array(DemoSchedulingHoursSchema).length(7),
    providers: z.array(DemoScheduleProviderSchema).min(1).max(SCHEDULING_LIMITS.providers),
    appointmentTypes: z
        .array(DemoAppointmentTypeSchema)
        .min(1)
        .max(SCHEDULING_LIMITS.appointmentTypes),
    reminderPolicy: DemoReminderPolicySchema,
    publicBookingPath: DemoPublicBookingPathSchema,
    /**
     * PA-12. The websites whose pages may show the booking widget's form; on any other page the
     * widget shows only a link to the booking page. Read as none when absent (a clinic that never
     * saved it).
     */
    websiteOrigins: WebsiteOriginsSchema.default([]),
};
function refineUniqueIds(path, entries, context) {
    const ids = entries.map((entry) => entry.id);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({
            code: "custom",
            message: `${path} identifiers must be unique`,
            path: [path],
        });
    }
}
function refineSchedulingSettings(value, context) {
    const weekdays = value.clinicHours.map((hours) => hours.weekday);
    if (new Set(weekdays).size !== DemoSchedulingWeekdaySchema.options.length) {
        context.addIssue({
            code: "custom",
            message: "Clinic hours must contain each weekday exactly once",
            path: ["clinicHours"],
        });
    }
    refineUniqueIds("providers", value.providers, context);
    refineUniqueIds("appointmentTypes", value.appointmentTypes, context);
}
function refineCalendarReferences(path, entries, settings, context) {
    refineUniqueIds(path, entries, context);
    const providerIds = new Set(settings.providers.map((provider) => provider.id));
    const appointmentTypeIds = new Set(settings.appointmentTypes.map((type) => type.id));
    entries.forEach((entry, index) => {
        if (!providerIds.has(entry.providerId)) {
            context.addIssue({
                code: "custom",
                message: "Availability references an unknown provider",
                path: [path, index, "providerId"],
            });
        }
        entry.appointmentTypeIds.forEach((appointmentTypeId, typeIndex) => {
            if (!appointmentTypeIds.has(appointmentTypeId)) {
                context.addIssue({
                    code: "custom",
                    message: "Availability references an unknown appointment type",
                    path: [path, index, "appointmentTypeIds", typeIndex],
                });
            }
        });
        entry.durationOverrides.forEach((override, overrideIndex) => {
            if (!appointmentTypeIds.has(override.appointmentTypeId) ||
                (entry.appointmentTypeIds.length > 0 &&
                    !entry.appointmentTypeIds.includes(override.appointmentTypeId))) {
                context.addIssue({
                    code: "custom",
                    message: "Duration override must reference an allowed appointment type",
                    path: [path, index, "durationOverrides", overrideIndex, "appointmentTypeId"],
                });
            }
        });
    });
}
export const DemoSchedulingConfigurationSchema = z
    .object({
    ...schedulingSettingsShape,
    providerAvailability: z
        .array(DemoProviderAvailabilityRuleSchema)
        .max(SCHEDULING_LIMITS.availabilityRules),
    /** Date overrides from the clinic's today onward. */
    availabilityOverrides: z
        .array(DemoAvailabilityOverrideSchema)
        .max(SCHEDULING_LIMITS.availabilityOverrides)
        .default([]),
})
    .strict()
    .superRefine((value, context) => {
    refineSchedulingSettings(value, context);
    refineCalendarReferences("providerAvailability", value.providerAvailability, value, context);
    refineCalendarReferences("availabilityOverrides", value.availabilityOverrides, value, context);
    refineNoOverlaps(value.providerAvailability, (left, right) => left.providerId === right.providerId && left.weekday === right.weekday, "providerAvailability", "Overlapping provider availability rules cannot cover the same appointment type", context);
    refineNoOverlaps(value.availabilityOverrides, (left, right) => left.providerId === right.providerId && left.date === right.date, "availabilityOverrides", "Overlapping date overrides cannot cover the same appointment type", context);
});
/**
 * What a durable settings save writes: everything but the physician calendars, which each
 * calendar save owns. Unlike the read, every newer key is required, so a client that leaves
 * one out cannot quietly reset it (for example reopen existing-patients-only booking to anyone).
 */
export const SchedulingSettingsSchema = z
    .object({
    ...schedulingSettingsShape,
    onlineBookingAudience: DemoOnlineBookingAudienceSchema,
    newPatientQuestionnaireRequired: z.boolean(),
    physicianChoice: DemoPhysicianChoiceSchema,
    cancellationCutoffMinutes: DemoCancellationCutoffMinutesSchema,
    patientRescheduling: PatientReschedulingSchema,
    websiteOrigins: WebsiteOriginsSchema,
    appointmentTypes: z
        .array(DemoAppointmentTypeSchema.extend({
        physicianChoice: DemoAppointmentTypePhysicianChoiceSchema,
    }))
        .min(1)
        .max(SCHEDULING_LIMITS.appointmentTypes),
    /** The durable booking link: /book/ and the clinic slug (3-80 characters). */
    publicBookingPath: z
        .string()
        .regex(/^\/book\/(?=[a-z0-9-]{3,80}$)[a-z0-9]+(?:-[a-z0-9]+)*$/, "Booking link must be /book/ followed by 3-80 lowercase letters, digits and single hyphens"),
})
    .strict()
    .superRefine(refineSchedulingSettings);
export const ProviderAvailabilityRuleInputSchema = z
    .object({ weekday: DemoSchedulingWeekdaySchema, ...availabilityWindowShape })
    .strict()
    .superRefine(refineAvailabilityWindow);
export const ProviderAvailabilityOverrideInputSchema = z
    .object({ date: LocalDateSchema, ...availabilityWindowShape })
    .strict()
    .superRefine(refineAvailabilityWindow);
/**
 * One physician's calendar as a calendar save writes it. The server assigns the ids, checks the
 * visit types, and keeps override dates between the clinic's today and
 * SCHEDULING_LIMITS.availabilityOverrideHorizonDays ahead. Leave is recorded as schedule blocks.
 */
export const ProviderAvailabilitySchema = z
    .object({
    rules: z
        .array(ProviderAvailabilityRuleInputSchema)
        .max(SCHEDULING_LIMITS.availabilityRulesPerProvider),
    overrides: z
        .array(ProviderAvailabilityOverrideInputSchema)
        .max(SCHEDULING_LIMITS.availabilityOverridesPerProvider),
})
    .strict()
    .superRefine((value, context) => {
    refineNoOverlaps(value.rules, (left, right) => left.weekday === right.weekday, "rules", "Overlapping provider availability rules cannot cover the same appointment type", context);
    refineNoOverlaps(value.overrides, (left, right) => left.date === right.date, "overrides", "Overlapping date overrides cannot cover the same appointment type", context);
});
/**
 * The sex a booking records: the registration set (female, male, other,
 * unknown = "Prefer not to say"), so booking, intake and registration agree
 * (PA-2).
 */
export const DemoAppointmentPatientSexSchema = PatientAdministrativeSexSchema;
export const DemoAppointmentPatientSchema = z
    .object({
    kind: z.enum(["existing", "new", "returning"]),
    patientId: DemoSchedulingIdSchema.optional(),
    displayName: ShortTextSchema,
    dateOfBirth: LocalDateSchema,
    sex: DemoAppointmentPatientSexSchema,
    phone: z.string().trim().min(1).max(60),
    email: z.string().trim().email().max(254).or(z.literal("")),
})
    .strict()
    .superRefine((value, context) => {
    if (value.kind === "existing" && value.patientId === undefined) {
        context.addIssue({
            code: "custom",
            message: "Existing patients require a patient identifier",
            path: ["patientId"],
        });
    }
});
/**
 * The two parts of a submitted pre-visit questionnaire that staff review, each marked reviewed
 * on its own (PA-6). `administrative` is identity and coverage: any scheduling role reviews it.
 * `clinical` is allergies, medications, medical history, pregnancy and symptoms: physicians and
 * nurses only, and the server leaves it out of every read for front desk and clinic admins.
 */
export const SchedulingIntakeSectionSchema = z.enum(["administrative", "clinical"]);
/**
 * awaiting_review: not yet marked reviewed, and the review is open.
 * reviewed: someone marked it reviewed; it stays reviewed after the review closes.
 * expired: never marked reviewed, and the review has closed.
 */
export const SchedulingIntakeSectionStateSchema = z.enum([
    "awaiting_review",
    "reviewed",
    "expired",
]);
/** reviewedAt (and the reviewer's name, where given) is present exactly when reviewed. */
function refineIntakeSectionReview(value, context, withName) {
    const reviewed = value.state === "reviewed";
    if (reviewed !== (value.reviewedAt !== undefined)) {
        context.addIssue({
            code: "custom",
            message: "A reviewed section names when it was reviewed, and only a reviewed one",
            path: ["reviewedAt"],
        });
    }
    if (withName && reviewed !== (value.reviewedByName !== undefined)) {
        context.addIssue({
            code: "custom",
            message: "A reviewed section names who reviewed it, and only a reviewed one",
            path: ["reviewedByName"],
        });
    }
}
/** One section's state on the schedule read. */
export const SchedulingIntakeSectionSummarySchema = z
    .object({
    state: SchedulingIntakeSectionStateSchema,
    reviewedAt: IsoDateTimeSchema.optional(),
})
    .strict()
    .superRefine((value, context) => refineIntakeSectionReview(value, context, false));
/** One section's review in the intake read: the summary plus the reviewer's display name. */
export const SchedulingIntakeSectionReviewSchema = z
    .object({
    state: SchedulingIntakeSectionStateSchema,
    reviewedAt: IsoDateTimeSchema.optional(),
    /** The reviewer's display name, or "A staff member"; never an email address. */
    reviewedByName: z.string().trim().min(1).max(200).optional(),
})
    .strict()
    .superRefine((value, context) => refineIntakeSectionReview(value, context, true));
/**
 * Whether each section's state agrees with whether the review is still open: an open review has
 * no expired section and a closed one none awaiting review.
 */
export function refineIntakeReviewOpen(open, states, context, pathOf = (section) => [section, "state"]) {
    for (const section of SchedulingIntakeSectionSchema.options) {
        if (states[section] === (open ? "expired" : "awaiting_review")) {
            context.addIssue({
                code: "custom",
                message: open
                    ? "An open review has no expired section"
                    : "A closed review has no section awaiting review",
                path: pathOf(section),
            });
        }
    }
}
/**
 * A durable appointment's questionnaire review on the schedule read, present only when a
 * questionnaire was submitted (PA-6). Rows show a pill and a "Review intake" action from it
 * without fetching the answers, which are read (and audited) only when the review is opened.
 */
export const SchedulingIntakeReviewSummarySchema = z
    .object({
    submittedAt: IsoDateTimeSchema,
    /**
     * Whether staff may still apply answers to the chart and mark sections reviewed. The review
     * closes at the end of the clinic's day on which the visit ended (completed, cancelled or no
     * show), or on which a visit that never arrived was booked, so a walk-in finished minutes
     * after the answers came in can still be applied that day. Reading stays possible.
     */
    open: z.boolean(),
    administrative: SchedulingIntakeSectionSummarySchema,
    /** Present only for a viewer who reviews health answers (physician or nurse). */
    clinical: SchedulingIntakeSectionSummarySchema.optional(),
})
    .strict()
    .superRefine((value, context) => refineIntakeReviewOpen(value.open, { administrative: value.administrative.state, clinical: value.clinical?.state }, context));
/**
 * The sections the viewer still has to review: the ones the server sent (so only those the
 * viewer may review) that await review, clinical first. Empty once the review closed.
 */
export function intakeSectionsToReview(summary) {
    if (summary === undefined || !summary.open)
        return [];
    const sections = [];
    if (summary.clinical?.state === "awaiting_review")
        sections.push("clinical");
    if (summary.administrative.state === "awaiting_review")
        sections.push("administrative");
    return sections;
}
/**
 * Whether a returning patient's online booking found the patient's chart (PA-10 /
 * EXECDEV-217): the state of the charts matched when the patient booked, on the staff schedule
 * read only. It never says which charts. Those are read, and audited, only when staff open the
 * booking's matches (SchedulingPatientMatchesSchema, basis booking), so the schedule poll never
 * names a chart. Counting the charts staff have not ruled out ("mismatch"):
 *
 * - likely: exactly one chart the booking can be linked to (same date of birth).
 * - several: more than one such chart.
 * - none: no chart matched.
 * - mismatch: charts matched, and staff ruled every one out.
 * - card_other_dob: no chart can be linked as it stands, but the health card the patient gave (or
 *   verified) is on a chart with another date of birth: the strongest sign of a date of birth
 *   typed wrong. Staff check the patient's card, then link that chart taking its date of birth
 *   for the booking (ReconcileSchedulingPatientRequestSchema, correctBookingDateOfBirth), or rule
 *   it out.
 */
export const SchedulingPatientMatchStateSchema = z.enum([
    "likely",
    "several",
    "none",
    "mismatch",
    "card_other_dob",
]);
/** A booking's chart-match state on the schedule read: the state alone, never a chart. */
export const SchedulingPatientMatchSummarySchema = z
    .object({ state: SchedulingPatientMatchStateSchema })
    .strict();
/** States whose matched charts still await a staff decision: link one, or rule them out. */
const UNRESOLVED_PATIENT_MATCH_STATES = new Set([
    "likely",
    "several",
    "card_other_dob",
]);
/**
 * Whether charts matched at booking still await a staff decision (likely, several or
 * card_other_dob). The row's Review booking action (appointmentRowState's unresolvedChartMatch)
 * and the server's refusal to ask the patient for their details meanwhile
 * (patient_match_review_required) follow the same rule.
 */
export function schedulingPatientMatchUnresolved(match) {
    return match !== undefined && UNRESOLVED_PATIENT_MATCH_STATES.has(match.state);
}
const PRE_ARRIVAL_STATUSES = new Set(["booked", "confirmed"]);
/**
 * The patient confirmed or cancelled the booking themselves, from their manage link (PA-9), and
 * that is still its status: kind is the status, at when the patient did it, and
 * cancellationReason the reason they chose (when they gave one). Staff see "Confirmed by patient"
 * or "Cancelled by patient" (SCHEDULING_PATIENT_CHANGE_STAFF_LABELS).
 */
export const SchedulingPatientChangeSchema = z
    .object({
    kind: z.enum(["confirmed", "cancelled"]),
    at: IsoDateTimeSchema,
    cancellationReason: PatientCancellationReasonSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.cancellationReason !== undefined && value.kind !== "cancelled") {
        context.addIssue({
            code: "custom",
            message: "Only a cancellation has a reason",
            path: ["cancellationReason"],
        });
    }
});
/** The booking's status chip when the patient made its status (lead decision L8). */
export const SCHEDULING_PATIENT_CHANGE_STAFF_LABELS = {
    confirmed: "Confirmed by patient",
    cancelled: "Cancelled by patient",
};
export const DemoAppointmentSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    version: z.number().int().positive().optional(),
    patient: DemoAppointmentPatientSchema,
    providerId: DemoSchedulingIdSchema,
    appointmentTypeId: DemoSchedulingIdSchema,
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    status: DemoAppointmentStatusSchema,
    source: DemoAppointmentSourceSchema,
    intakeStatus: DemoScheduledIntakeStatusSchema,
    reason: ShortTextSchema,
    notes: OptionalTextSchema,
    encounterId: DemoSchedulingIdSchema.optional(),
    /**
     * The clinic assigned the physician (physician choice clinic_assigns): the patient was never
     * shown one, and staff may reassign it.
     */
    clinicAssigned: z.literal(true).optional(),
    /**
     * The submitted questionnaire's review (durable schedule only, PA-6). Absent when nothing
     * was submitted, and on every payload from before PA-6. Never on patient-facing receipts.
     */
    intakeReview: SchedulingIntakeReviewSummarySchema.optional(),
    /**
     * Whether the charts matched when a returning patient booked online still await staff
     * (durable schedule only, PA-10). Only on such a booking while it has no chart and the patient
     * has not arrived: absent once a chart is linked or the visit is cancelled, missed or
     * completed, on every other booking, and on every payload from before PA-10. Never on
     * patient-facing receipts.
     */
    patientMatch: SchedulingPatientMatchSummarySchema.optional(),
    /**
     * The text or email to this booking that staff should know about (durable schedule only,
     * PA-7): one that was not delivered, may not have been sent, or was stopped by the patient's
     * STOP reply. Only before arrival (booked or confirmed). Absent when every message is fine, on
     * every other booking, and on every payload from before PA-7. Never on patient-facing
     * receipts. See SchedulingMessageAttentionSchema for which message it names.
     */
    messageAttention: SchedulingMessageAttentionSchema.optional(),
    /**
     * The patient confirmed or cancelled it from their manage link and staff have not changed
     * its status or time since (durable schedule only, PA-9). Absent otherwise: when staff made
     * the status, after a staff time change of a booking the patient had confirmed (lead
     * decision L8), and on every payload from before PA-9. Never on patient-facing receipts.
     */
    patientChange: SchedulingPatientChangeSchema.optional(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (Date.parse(value.startAt) >= Date.parse(value.endAt)) {
        context.addIssue({
            code: "custom",
            message: "Appointment must end after it starts",
            path: ["endAt"],
        });
    }
    if (value.patientChange !== undefined && value.patientChange.kind !== value.status) {
        context.addIssue({
            code: "custom",
            message: "A patient's change names the booking's current status",
            path: ["patientChange", "kind"],
        });
    }
    if (value.messageAttention !== undefined && !PRE_ARRIVAL_STATUSES.has(value.status)) {
        context.addIssue({
            code: "custom",
            message: "Only a booking before arrival has a message to look at",
            path: ["messageAttention"],
        });
    }
    if (value.patientMatch !== undefined &&
        (value.patient.kind !== "returning" ||
            value.patient.patientId !== undefined ||
            !PRE_ARRIVAL_STATUSES.has(value.status))) {
        context.addIssue({
            code: "custom",
            message: "Only a returning patient's booking without a chart, before arrival, has a chart match state",
            path: ["patientMatch"],
        });
    }
});
export const DemoScheduleBlockSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    providerId: DemoSchedulingIdSchema.optional(),
    kind: z.enum(["clinic_closed", "provider_unavailable", "vacation", "administrative"]),
    label: ShortTextSchema,
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (Date.parse(value.startAt) >= Date.parse(value.endAt)) {
        context.addIssue({
            code: "custom",
            message: "Blocked time must end after it starts",
            path: ["endAt"],
        });
    }
    if ((value.kind === "provider_unavailable" || value.kind === "vacation") &&
        value.providerId === undefined) {
        context.addIssue({
            code: "custom",
            message: "Provider unavailability and vacation blocks require a provider",
            path: ["providerId"],
        });
    }
    if (value.kind === "clinic_closed" && value.providerId !== undefined) {
        context.addIssue({
            code: "custom",
            message: "Clinic closure blocks must apply to the whole clinic",
            path: ["providerId"],
        });
    }
});
export const DemoReminderReceiptSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    appointmentId: DemoSchedulingIdSchema,
    kind: DemoReminderKindSchema,
    channel: DemoReminderChannelSchema,
    recipientMasked: z.string().trim().min(1).max(100),
    status: z.literal("simulated"),
    provider: z.literal("synthetic_placeholder"),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export const DemoSchedulingStateSchema = z
    .object({
    configuration: DemoSchedulingConfigurationSchema,
    appointments: z.array(DemoAppointmentSchema).max(5_000),
    blocks: z.array(DemoScheduleBlockSchema).max(SCHEDULING_LIMITS.stateBlocks),
    reminderReceipts: z.array(DemoReminderReceiptSchema).max(10_000),
})
    .strict()
    .superRefine((value, context) => {
    const providerIds = new Set(value.configuration.providers.map((provider) => provider.id));
    const appointmentTypeIds = new Set(value.configuration.appointmentTypes.map((appointmentType) => appointmentType.id));
    const appointmentIds = new Set(value.appointments.map((appointment) => appointment.id));
    value.appointments.forEach((appointment, index) => {
        if (!providerIds.has(appointment.providerId)) {
            context.addIssue({
                code: "custom",
                message: "Appointment references an unknown provider",
                path: ["appointments", index, "providerId"],
            });
        }
        if (!appointmentTypeIds.has(appointment.appointmentTypeId)) {
            context.addIssue({
                code: "custom",
                message: "Appointment references an unknown appointment type",
                path: ["appointments", index, "appointmentTypeId"],
            });
        }
        if (appointment.patient.kind !== "new" &&
            (appointment.intakeStatus === "required" || appointment.intakeStatus === "sent")) {
            context.addIssue({
                code: "custom",
                message: "Only new patients can have pending scheduled intake",
                path: ["appointments", index, "intakeStatus"],
            });
        }
    });
    value.blocks.forEach((block, index) => {
        if (block.providerId !== undefined && !providerIds.has(block.providerId)) {
            context.addIssue({
                code: "custom",
                message: "Schedule block references an unknown provider",
                path: ["blocks", index, "providerId"],
            });
        }
    });
    value.reminderReceipts.forEach((receipt, index) => {
        if (!appointmentIds.has(receipt.appointmentId)) {
            context.addIssue({
                code: "custom",
                message: "Reminder receipt references an unknown appointment",
                path: ["reminderReceipts", index, "appointmentId"],
            });
        }
    });
});
/** An availability search runs forward over at most 63 local dates. */
export function refineAvailabilityDateRange(value, context) {
    const start = Date.parse(`${value.fromDate}T00:00:00Z`);
    const end = Date.parse(`${value.toDate}T00:00:00Z`);
    if (start > end) {
        context.addIssue({
            code: "custom",
            message: "Availability start date must not follow the end date",
            path: ["toDate"],
        });
    }
    if (end - start > 62 * 86_400_000) {
        context.addIssue({
            code: "custom",
            message: "Availability may span at most 63 days",
            path: ["toDate"],
        });
    }
}
export const DemoAvailabilityQuerySchema = z
    .object({
    fromDate: LocalDateSchema,
    toDate: LocalDateSchema,
    appointmentTypeId: DemoSchedulingIdSchema,
    providerId: DemoSchedulingIdSchema.optional(),
    audience: z.enum(["staff", "patient"]).default("staff"),
})
    .strict()
    .superRefine(refineAvailabilityDateRange);
export const DemoAvailabilitySlotSchema = z
    .object({
    providerId: DemoSchedulingIdSchema,
    appointmentTypeId: DemoSchedulingIdSchema,
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
})
    .strict();
export const DemoAvailabilityResponseSchema = z
    .object({
    timeZone: IanaTimeZoneSchema,
    slots: z.array(DemoAvailabilitySlotSchema).max(10_000),
})
    .strict();
export const CreateDemoAppointmentRequestSchema = z
    .object({
    patient: DemoAppointmentPatientSchema,
    providerId: DemoSchedulingIdSchema.optional(),
    appointmentTypeId: DemoSchedulingIdSchema,
    startAt: IsoDateTimeSchema,
    source: DemoAppointmentSourceSchema,
    reason: ShortTextSchema,
    notes: OptionalTextSchema.default(""),
    sendConfirmation: z.boolean().default(true),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const UpdateDemoAppointmentRequestSchema = z
    .object({
    expectedUpdatedAt: IsoDateTimeSchema,
    providerId: DemoSchedulingIdSchema.optional(),
    appointmentTypeId: DemoSchedulingIdSchema.optional(),
    startAt: IsoDateTimeSchema.optional(),
    reason: ShortTextSchema.optional(),
    notes: OptionalTextSchema.optional(),
    patientId: DemoSchedulingIdSchema.optional(),
    encounterId: DemoSchedulingIdSchema.optional(),
})
    .strict();
export const UpdateDemoAppointmentStatusRequestSchema = z
    .object({
    expectedUpdatedAt: IsoDateTimeSchema,
    status: DemoAppointmentStatusSchema,
})
    .strict();
export const CreateDemoScheduleBlockRequestSchema = z
    .object({
    providerId: DemoSchedulingIdSchema.optional(),
    kind: z.enum(["clinic_closed", "provider_unavailable", "vacation", "administrative"]),
    label: ShortTextSchema,
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.kind === "provider_unavailable" || value.kind === "vacation") &&
        value.providerId === undefined) {
        context.addIssue({
            code: "custom",
            message: "Provider unavailability and vacation blocks require a provider",
            path: ["providerId"],
        });
    }
    if (value.kind === "clinic_closed" && value.providerId !== undefined) {
        context.addIssue({
            code: "custom",
            message: "Clinic closure blocks must apply to the whole clinic",
            path: ["providerId"],
        });
    }
});
export const RemoveDemoScheduleBlockRequestSchema = z
    .object({
    expectedCreatedAt: IsoDateTimeSchema,
})
    .strict();
export const UpdateDemoSchedulingConfigurationRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    configuration: DemoSchedulingConfigurationSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.configuration.version !== value.expectedVersion) {
        context.addIssue({
            code: "custom",
            message: "Configuration version must match the expected version",
            path: ["configuration", "version"],
        });
    }
});
export const SendDemoReminderRequestSchema = z
    .object({
    kind: DemoReminderKindSchema,
    channel: DemoReminderChannelSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const CreateDemoScheduledIntakeInvitationRequestSchema = z
    .object({
    delivery: DemoScheduledIntakeDeliverySchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const DemoScheduledIntakeInvitationEmailReceiptSchema = z
    .object({
    id: DemoSchedulingIdSchema,
    appointmentId: DemoSchedulingIdSchema,
    recipientMasked: z.string().trim().min(1).max(100),
    status: z.literal("simulated"),
    provider: z.literal("synthetic_placeholder"),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export const DemoScheduledIntakeInvitationReceiptSchema = z
    .object({
    appointmentId: DemoSchedulingIdSchema,
    intakeStatus: z.enum(["required", "sent"]),
    delivery: DemoScheduledIntakeDeliverySchema,
    questionnaireUrl: DemoScheduledIntakePathSchema,
    emailReceipt: DemoScheduledIntakeInvitationEmailReceiptSchema.optional(),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export const DemoScheduledIntakeSessionSchema = z
    .object({
    bookingReference: DemoSchedulingIdSchema,
    clinicName: ShortTextSchema,
    clinicAddress: ShortTextSchema,
    providerDisplayName: ShortTextSchema,
    appointmentTypeName: ShortTextSchema,
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    intakeStatus: z.enum(["required", "sent", "completed"]),
    prefill: PatientQuestionnaireDraftSchema,
    notice: z.string().trim().min(1).max(500),
})
    .strict();
export const CompleteDemoScheduledIntakeRequestSchema = z
    .object({
    intake: PatientQuestionnaireSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const DemoScheduledIntakeCompletionReceiptSchema = z
    .object({
    bookingReference: DemoSchedulingIdSchema,
    completionReference: DemoSchedulingIdSchema,
    intakeStatus: z.literal("completed"),
    completedAt: IsoDateTimeSchema,
    notice: z.string().trim().min(1).max(500),
})
    .strict();
export const DemoAppointmentMutationReceiptSchema = z
    .object({
    appointment: DemoAppointmentSchema,
    reminders: z.array(DemoReminderReceiptSchema).max(2),
})
    .strict();
/**
 * What the booking page may know about a clinic. It never carries the cancellation cutoff, the
 * questionnaire setting, or anything about patients; while booking is off or walk-in only it
 * lists no physicians or visit types.
 */
export const DemoPublicSchedulingConfigurationSchema = z
    .object({
    clinicName: ShortTextSchema,
    clinicAddress: ShortTextSchema,
    /** The site phone for "call the clinic", when one is on file (3-40 characters). */
    clinicPhone: z.string().trim().min(3).max(40).optional(),
    timeZone: IanaTimeZoneSchema,
    mode: DemoSchedulingModeSchema,
    onlineBookingEnabled: z.boolean(),
    onlineBookingAudience: DemoOnlineBookingAudienceSchema.default("anyone"),
    /** False unless booking is open to anyone and the clinic takes new patients online. */
    allowNewPatientsOnline: z.boolean(),
    bookingWindowDays: z.number().int().min(1).max(365),
    minimumLeadMinutes: z.number().int().min(0).max(43_200),
    /** Only physicians the patient may choose; empty when the clinic assigns them. */
    providers: z.array(DemoScheduleProviderSchema.pick({
        id: true,
        displayName: true,
        credentials: true,
        active: true,
        acceptingOnlineBookings: true,
    })),
    appointmentTypes: z.array(DemoAppointmentTypeSchema.pick({
        id: true,
        name: true,
        description: true,
        durationMinutes: true,
        visitMode: true,
        active: true,
        patientBookable: true,
        newPatientsAllowed: true,
    }).extend({
        /** False when the clinic assigns the physician for this visit type. */
        patientChoosesPhysician: z.boolean().default(true),
    })),
    publicBookingPath: DemoPublicBookingPathSchema,
})
    .strict();
export const DemoPublicBookingPatientSchema = z
    .object({
    kind: z.enum(["new", "returning"]),
    displayName: ShortTextSchema,
    dateOfBirth: LocalDateSchema,
    sex: DemoAppointmentPatientSexSchema,
    phone: z.string().trim().min(1).max(60),
    email: z.string().trim().email().max(254).or(z.literal("")),
})
    .strict();
export const CreateDemoPublicBookingRequestSchema = z
    .object({
    patient: DemoPublicBookingPatientSchema,
    providerId: DemoSchedulingIdSchema.optional(),
    appointmentTypeId: DemoSchedulingIdSchema,
    startAt: IsoDateTimeSchema,
    reason: ShortTextSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const DemoPublicBookingReceiptSchema = z
    .object({
    bookingReference: DemoSchedulingIdSchema,
    patientDisplayName: ShortTextSchema,
    providerId: DemoSchedulingIdSchema,
    appointmentTypeId: DemoSchedulingIdSchema,
    startAt: IsoDateTimeSchema,
    endAt: IsoDateTimeSchema,
    status: z.literal("booked"),
    intakeStatus: DemoScheduledIntakeStatusSchema,
    intakeQuestionnaireUrl: DemoScheduledIntakePathSchema.optional(),
    reminders: z.array(DemoReminderReceiptSchema).max(2),
    notice: z.string().trim().min(1).max(500),
})
    .strict();
//# sourceMappingURL=demo-scheduling.js.map